import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Toast from '../../../components/Common/Toast';
import activoService from '../../../services/activoService';
import ActivoInicialForm from './ActivoInicialForm';
import {
  analyzeActivosInicialesImport,
  downloadActivosInicialesTemplate,
  executeActivosInicialesImport,
  summarizeImportResult,
} from '../../../utils/activosInicialesExcelImport';

const MODES = [
  { id: 'manual', label: 'Registro manual' },
  { id: 'excel', label: 'Importar Excel' },
];

const CargarActivosIniciales = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState('manual');
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [savingManual, setSavingManual] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const handleDownloadTemplate = async () => {
    try {
      await downloadActivosInicialesTemplate();
    } catch {
      setToast({ isOpen: true, message: 'No se pudo generar la plantilla', type: 'error' });
    }
  };

  const handleFileChange = async (event) => {
    const selected = event.target.files?.[0];
    setFile(selected || null);
    setPreview(null);
    setImportErrors([]);
    if (!selected) return;

    setAnalyzing(true);
    try {
      const analysis = await analyzeActivosInicialesImport(selected);
      if (!analysis.success) {
        setToast({ isOpen: true, message: analysis.fatalMessage, type: 'error' });
        return;
      }
      setPreview(analysis);
    } catch {
      setToast({ isOpen: true, message: 'No se pudo leer el archivo Excel', type: 'error' });
    } finally {
      setAnalyzing(false);
    }
  };

  const handleManualSubmit = async (payload) => {
    setSavingManual(true);
    try {
      const result = await activoService.createInitialActivo(payload);
      if (result.created > 0) {
        setToast({ isOpen: true, message: 'Activo inicial registrado correctamente', type: 'success' });
        setTimeout(() => navigate('/activos/listado'), 1200);
        return;
      }
      const errMsg = result.errors?.[0]?.message || result.message || 'No se pudo registrar el activo';
      setToast({ isOpen: true, message: errMsg, type: 'error' });
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al registrar activo inicial',
        type: 'error',
      });
    } finally {
      setSavingManual(false);
    }
  };

  const handleImport = async () => {
    if (!preview?.validTasks?.length) {
      setToast({ isOpen: true, message: 'No hay filas válidas para importar', type: 'warning' });
      return;
    }

    setImporting(true);
    setImportErrors([]);
    try {
      const result = await executeActivosInicialesImport(preview.validTasks);
      const message = summarizeImportResult(result);
      setToast({
        isOpen: true,
        message,
        type: result.created > 0 ? (result.errors.length ? 'warning' : 'success') : 'error',
      });
      if (result.errors.length) {
        setImportErrors(result.errors);
      }
      if (result.created > 0 && result.errors.length === 0) {
        setTimeout(() => navigate('/activos/listado'), 1500);
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar activos iniciales',
        type: 'error',
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-4">
      <div className="max-w-4xl mx-auto space-y-3">
        <div>
          <nav className="text-xs text-gray-500 mb-0.5">
            <Link to="/activos/listado" className="hover:text-teal-600">← Volver al listado</Link>
          </nav>
          <h1 className="text-xl font-bold text-gray-800">Activos iniciales</h1>
          <p className="text-xs text-gray-500 mt-1">
            Registre el inventario histórico que ya existía antes de usar el sistema.
            Los activos nuevos se crean automáticamente desde documentos de compra.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="flex border-b border-gray-100 px-2 bg-gray-50/60">
            {MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setMode(m.id)}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  mode === m.id
                    ? 'border-teal-600 text-teal-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          <div className="p-4">
            {mode === 'manual' ? (
              <ActivoInicialForm onSubmit={handleManualSubmit} loading={savingManual} />
            ) : (
              <div className="space-y-4">
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  Cada fila del Excel debe incluir costo sin IGV, fecha de inicio y vida útil en años.
                </p>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="px-3 py-1.5 text-sm border border-teal-600 text-teal-700 rounded-md hover:bg-teal-50"
                  >
                    Descargar plantilla
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700"
                  >
                    Seleccionar archivo
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>

                {file && (
                  <p className="text-xs text-gray-600">
                    Archivo: <strong>{file.name}</strong>
                    {analyzing ? ' — analizando…' : null}
                  </p>
                )}

                {preview && (
                  <>
                    <div className="flex flex-wrap gap-3 text-xs">
                      <span>Filas: <strong>{preview.previewRows.length}</strong></span>
                      <span className="text-teal-700">Válidas: <strong>{preview.validCount}</strong></span>
                      {preview.errorCount > 0 && (
                        <span className="text-red-700">Errores: <strong>{preview.errorCount}</strong></span>
                      )}
                    </div>

                    <div className="overflow-x-auto border rounded-md max-h-80">
                      <table className="min-w-full divide-y divide-gray-200 text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-2 py-1.5 text-left font-medium text-gray-500">Fila</th>
                            <th className="px-2 py-1.5 text-left font-medium text-gray-500">Código</th>
                            <th className="px-2 py-1.5 text-left font-medium text-gray-500">Nombre</th>
                            <th className="px-2 py-1.5 text-left font-medium text-gray-500">Área</th>
                            <th className="px-2 py-1.5 text-right font-medium text-gray-500">Costo</th>
                            <th className="px-2 py-1.5 text-left font-medium text-gray-500">Estado</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {preview.previewRows.map((row) => (
                            <tr
                              key={row.excelRow}
                              className={row.status === 'error' ? 'bg-red-50/60' : 'hover:bg-gray-50'}
                            >
                              <td className="px-2 py-1.5">{row.excelRow}</td>
                              <td className="px-2 py-1.5 font-medium">{row.cols.codigo}</td>
                              <td className="px-2 py-1.5">{row.cols.nombre}</td>
                              <td className="px-2 py-1.5">{row.cols.area}</td>
                              <td className="px-2 py-1.5 text-right whitespace-nowrap">
                                {typeof row.cols.costo === 'number'
                                  ? `S/ ${row.cols.costo.toLocaleString('es-PE', { minimumFractionDigits: 2 })}`
                                  : row.cols.costo}
                              </td>
                              <td className="px-2 py-1.5">
                                {row.status === 'valid' ? (
                                  <span className="text-teal-700 font-medium">OK</span>
                                ) : (
                                  <span className="text-red-700">{row.issue}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {importErrors.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-xs text-red-900">
                        <p className="font-medium mb-1">Filas no cargadas:</p>
                        <ul className="list-disc pl-4 space-y-0.5">
                          {importErrors.map((err, idx) => (
                            <li key={`${err.row}-${idx}`}>
                              {err.row ? `Fila ${err.row}` : 'General'}
                              {err.code ? ` (${err.code})` : ''}: {err.message}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleImport}
                        disabled={importing || !preview.validCount}
                        className="px-4 py-2 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium disabled:opacity-50"
                      >
                        {importing ? 'Cargando…' : `Cargar ${preview.validCount} activo(s)`}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default CargarActivosIniciales;
